import {
  useIsTimelineExpanded,
  useDataDumpRows,
} from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import { DATA_DUMP_ROWS_FOR_SKU_OPERATOR_TIMELINE_INTERVAL } from "constants/intervals";
import {
  DATA_DUMP_ROWS_QUERY,
  midgardClients,
} from "providers/MidgardProvider";
import { useSelector } from "store/useSelector";
import { TimeRangeFinite } from "types/types";
import {
  useGetPageFromPath,
  useIsSingleProcessPage,
  useVisibleProcessIds,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightConfig } from "./FlightConfig/useFlightConfig";
import { useInterval } from "react-use";
import { SIGNALS_DEBUG_TIMELINES } from "components/Dashboard/Timeline/TimelineTooltip/TimelineTooltipPanels/signalValidationUtils";
import { MACHEMETRY_URL } from "providers/MachemetryProvider";
import { atom, useAtom } from "jotai";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import {
  useEffect,
  useRef,
  startTransition,
  useMemo,
  useCallback,
} from "react";
import { useTimelinesStore } from "components/Dashboard/Timeline/TimelineCardControls/timelinesStore";
import { useForceRerenderOnIntervalIfCurrentRange } from "./useForceRerenderOnIntervalIfCurrentRange";

// Throttling delay in milliseconds - wait this long after the last wheel event
const THROTTLE_WAIT = 1000; // 1 second - wait for wheel events to stop

export function DataDumpRowsGetterForSingleTimeline({
  processId,
}: {
  processId: string;
}) {
  const isSingleProcessPage = useIsSingleProcessPage();
  const visibleProcessIds = useVisibleProcessIds();
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const ckey = useCustomerKey();

  const [, setDataDumpRows] = useDataDumpRows();

  const [
    {
      general: { timelineTooltipShowCounts, multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  const [isTimelineExpanded] = useIsTimelineExpanded();
  const { visibleSubTimelines } = useTimelinesStore();

  const { isTier0Page } = useGetPageFromPath();

  const [dataDumpRanges] = useDataDumpRanges();
  const machemetryIds = isSingleProcessPage ? [processId] : visibleProcessIds;
  // Check if we need to update the process IDs we're tracking
  const processIdsChanged =
    machemetryIds.length !== dataDumpRanges.processIds.length ||
    machemetryIds.some(id => !dataDumpRanges.processIds.includes(id));

  // Calculate missing ranges that need to be fetched
  let rangesToFetch: { ge: number; lt: number }[] = [];

  if (visibleRange && visibleRange.ge && visibleRange.lt) {
    if (processIdsChanged) {
      // If process IDs changed, we need to fetch the entire range
      rangesToFetch = [{ ge: visibleRange.ge, lt: visibleRange.lt }];
    } else {
      // Otherwise, only fetch missing ranges
      rangesToFetch = getMissingRanges(dataDumpRanges.ranges, {
        ge: visibleRange.ge,
        lt: visibleRange.lt,
      });
    }
  }

  const shouldFetch =
    visibleRange &&
    visibleRange.ge &&
    visibleRange.lt &&
    (timelineTooltipShowCounts ||
      (isTimelineExpanded && Object.values(visibleSubTimelines).length > 0) ||
      isTier0Page) &&
    // Only fetch if there are missing ranges
    rangesToFetch.length > 0 &&
    visibleRange !== null;

  const forceRefreshKey = useForceRerenderOnIntervalIfCurrentRange();
  const focusRange = useSelector(state => state.legacy.focusRange);

  return shouldFetch ? (
    <>
      <QueryDataDumpRows
        key={`${forceRefreshKey}-${focusRange?.ge}-${focusRange?.lt}`}
        {...{
          visibleRange,
          ckey,
          multiplyNetworkRequestIntervals,
          setDataDumpRows,
          machemetryIds,
          processIdsChanged,
          rangesToFetch,
        }}
      />
      {isTimelineExpanded && visibleSubTimelines.grossRejectNetTicks && (
        <QueryMachemetryTicks />
      )}
    </>
  ) : null;
}

// TypeScript interfaces for our query structure
interface RangeData {
  ge: number;
  lt: number;
}

const QUERY_INTERVAL = 1000; // Process queue every 1 second

function QueryDataDumpRows({
  visibleRange,
  ckey,
  multiplyNetworkRequestIntervals,
  setDataDumpRows,
  machemetryIds,
  rangesToFetch,
}: {
  visibleRange: TimeRangeFinite | null;
  ckey: string;
  multiplyNetworkRequestIntervals: number;
  setDataDumpRows: any;
  machemetryIds: string[];
  rangesToFetch: { ge: number; lt: number }[];
}) {
  const client = midgardClients.get(ckey);

  // Use refs instead of state to avoid dependency loops
  const pendingRangesRef = useRef<RangeData[]>([]);
  const isProcessingRef = useRef(false);
  const lastProcessedRangeRef = useRef<TimeRangeFinite | null>(null);
  const processingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Process the queue of time ranges
  const processQueue = useCallback(() => {
    // Clear the timer ref
    processingTimerRef.current = null;

    // Skip if already processing or no ranges or no client
    if (
      isProcessingRef.current ||
      pendingRangesRef.current.length === 0 ||
      !client
    ) {
      return;
    }

    // Set processing flag
    isProcessingRef.current = true;

    // Get all pending ranges
    const allRanges = [...pendingRangesRef.current];

    // Clear pending ranges
    pendingRangesRef.current = [];

    // Merge overlapping ranges
    const mergedRanges = mergeOverlappingRanges(allRanges);

    // Find the overall min and max for the combined query
    const minGe = Math.min(...mergedRanges.map(r => r.ge));
    const maxLt = Math.max(...mergedRanges.map(r => r.lt));

    // Save this range as the last processed
    if (visibleRange) {
      lastProcessedRangeRef.current = {
        ge: minGe,
        lt: maxLt,
      };
    }

    // Execute the optimized query that covers all ranges
    console.log(
      `Fetching data dump rows from ${new Date(minGe)} to ${new Date(maxLt)}`
    );

    client
      .query({
        query: DATA_DUMP_ROWS_QUERY,
        variables: {
          ge: String(minGe),
          lt: String(maxLt),
          ckey,
          machemetryIds,
        },
      })
      .then(resp => {
        if (resp.data.dataDump) {
          machemetryIds.forEach(machemetryId => {
            const dataDumpRows =
              resp.data.dataDump.filter(d => d.machemetryId === machemetryId) ??
              [];

            setDataDumpRows(prev => ({
              ...prev,
              [machemetryId]: dataDumpRows,
            }));
          });
        } else {
          console.log("🚨 no dataDump found", resp.data);
        }

        // Reset processing flag
        isProcessingRef.current = false;

        // If new ranges were added during processing, schedule another process
        if (
          pendingRangesRef.current.length > 0 &&
          processingTimerRef.current === null
        ) {
          processingTimerRef.current = setTimeout(processQueue, QUERY_INTERVAL);
        }
      })
      .catch(error => {
        console.error("Error fetching data dump rows:", error);
        isProcessingRef.current = false;

        // Put the ranges back in the queue
        pendingRangesRef.current = [...pendingRangesRef.current, ...allRanges];

        // Try again later
        if (processingTimerRef.current === null) {
          processingTimerRef.current = setTimeout(
            processQueue,
            QUERY_INTERVAL * 2
          );
        }
      });
  }, [client, ckey, machemetryIds, setDataDumpRows, visibleRange]);

  // Function to add a range to the queue
  const addToQueue = useCallback(
    (range: TimeRangeFinite) => {
      if (!range) {
        return;
      }

      // First check if this is a zoom-in operation
      const isZoomIn =
        lastProcessedRangeRef.current &&
        lastProcessedRangeRef.current.ge <= range.ge &&
        lastProcessedRangeRef.current.lt >= range.lt;

      // Don't add to queue if no data is actually needed or if this is a zoom-in
      if (rangesToFetch.length === 0 || isZoomIn) {
        console.log(
          `[DataGetter] Skipping queue add - ${isZoomIn ? "zoom in" : "no data needed"}`,
          new Date()
        );
        return;
      }

      // Skip if this exact range was just processed
      if (
        lastProcessedRangeRef.current &&
        lastProcessedRangeRef.current.ge === range.ge &&
        lastProcessedRangeRef.current.lt === range.lt
      ) {
        console.log("[DataGetter] Skipping duplicate range", new Date());
        return;
      }

      // If we're zooming in (current range is fully within last processed range),
      // don't queue another fetch
      if (
        lastProcessedRangeRef.current &&
        lastProcessedRangeRef.current.ge <= range.ge &&
        lastProcessedRangeRef.current.lt >= range.lt
      ) {
        console.log("[DataGetter] Skipping - zoom in detected", new Date());
        return;
      }

      // Add to pending ranges
      pendingRangesRef.current.push({
        ge: range.ge,
        lt: range.lt,
      });

      // If not currently processing, schedule processing
      if (!isProcessingRef.current && processingTimerRef.current === null) {
        processingTimerRef.current = setTimeout(processQueue, 50);
      }
    },
    [processQueue, rangesToFetch]
  );

  // Add to queue when visibleRange changes
  useEffect(() => {
    if (visibleRange && client) {
      addToQueue(visibleRange);
    }
  }, [visibleRange, client, addToQueue]);

  // Set up regular polling
  useInterval(() => {
    if (visibleRange && client) {
      addToQueue(visibleRange);
    }
  }, DATA_DUMP_ROWS_FOR_SKU_OPERATOR_TIMELINE_INTERVAL * multiplyNetworkRequestIntervals);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
        processingTimerRef.current = null;
      }
    };
  }, []);

  // Set up regular processing interval
  useInterval(() => {
    // If we have pending ranges and we're not processing, trigger processing
    if (
      pendingRangesRef.current.length > 0 &&
      !isProcessingRef.current &&
      processingTimerRef.current === null
    ) {
      processingTimerRef.current = setTimeout(processQueue, 0);
    }
  }, QUERY_INTERVAL);

  return null;
}

export type MachemetryTick = {
  ts: number;
  v: {
    v: number;
    d: number;
  };
};

const machemetryTicksAtom = atom<{
  grossCount_0: MachemetryTick[];
  rejectCount_0: MachemetryTick[];
  netCount_0: MachemetryTick[];
}>({ grossCount_0: [], rejectCount_0: [], netCount_0: [] });

const ticksRangesAtom = atom<{
  processId: string | null;
  ranges: { ge: number; lt: number }[];
}>({ processId: null, ranges: [] });

// Track fetched ranges for data dump rows to avoid unnecessary refetching
const dataDumpRangesAtom = atom<{
  processIds: string[];
  ranges: { ge: number; lt: number }[];
}>({ processIds: [], ranges: [] });

export const useMachemetryTicks = () => useAtom(machemetryTicksAtom);
export const useTicksRanges = () => useAtom(ticksRangesAtom);
export const useDataDumpRanges = () => useAtom(dataDumpRangesAtom);

function QueryMachemetryTicks() {
  const [isTimelineExpanded] = useIsTimelineExpanded();

  // ensure we update machemetry ticks on a regular basis
  const randKey = useForceRerenderOnIntervalIfCurrentRange();
  return isTimelineExpanded ? (
    <QueryMachemetryTicksContent key={randKey} />
  ) : null;
}

function QueryMachemetryTicksContent() {
  const machemetryId = useProcessIdOnSingleProcessPage();
  const visibleRange = useSelector(s => s.legacy.visibleRange);

  const [, setMachemetryTicks] = useMachemetryTicks();
  const [ticksRanges, setTicksRanges] = useTicksRanges();
  const idToken = window.localStorage.getItem("id_token");
  const lastFetchedTime = useRef<number>(0);

  // Track if the current time is within the visible range
  const isCurrentTimeInVisibleRange = useMemo(() => {
    if (!visibleRange?.ge || !visibleRange?.lt) {
      return false;
    }
    const now = Date.now();
    return visibleRange.ge <= now && visibleRange.lt >= now;
  }, [visibleRange]);

  // Set up periodic refresh for current time ranges
  const refreshCurrentTimeData = useCallback(() => {
    if (isCurrentTimeInVisibleRange && machemetryId) {
      // If visible range includes current time, force reset of ranges near current time
      const now = Date.now() / 1000; // Convert to seconds
      const resetTime = now - 60; // Reset data from 1 minute ago to ensure fresh data

      console.log(
        "[MachemetryTicks] Refreshing data for current time range",
        new Date()
      );

      // Update ranges to force refetch of data near current time
      setTicksRanges(prev => ({
        ...prev,
        ranges: prev.ranges.filter(r => r.lt < resetTime), // Keep only historical ranges
      }));
    }
  }, [isCurrentTimeInVisibleRange, machemetryId, setTicksRanges]);

  // Initial refresh when current time comes into view
  useEffect(() => {
    if (isCurrentTimeInVisibleRange) {
      refreshCurrentTimeData();
    }
  }, [isCurrentTimeInVisibleRange, refreshCurrentTimeData]);

  // Set up interval for refreshing current time data every 30 seconds
  useInterval(
    refreshCurrentTimeData,
    isCurrentTimeInVisibleRange ? 30000 : null
  );

  // Refs for throttling and deduplication
  const throttleTimerRef = useRef<number | null>(null);
  const lastRangeStringRef = useRef("");
  const pendingRangeRef = useRef<{ ge: number; lt: number } | null>(null);

  // This effect handles throttling data fetching when the visible range changes
  useEffect(() => {
    if (!visibleRange || !machemetryId) {
      return;
    }

    const geSeconds = visibleRange.ge ? visibleRange.ge / 1000 : 0;
    const ltSeconds = visibleRange.lt ? visibleRange.lt / 1000 : 0;
    const currentRange = { ge: geSeconds, lt: ltSeconds };

    // Always store the most recent range request
    pendingRangeRef.current = currentRange;

    // If a timer is already running, let it complete with the latest range
    if (throttleTimerRef.current !== null) {
      return;
    }

    // Create a new timer - only execute after 1 second
    console.log(`[DataDumpRows] Starting throttle timer for ${machemetryId}`);

    throttleTimerRef.current = window.setTimeout(() => {
      // Get the most recent range that was requested during the throttle period
      const finalRange = pendingRangeRef.current;
      if (!finalRange) {
        throttleTimerRef.current = null;
        return;
      }

      // Reset pending range
      pendingRangeRef.current = null;

      // Generate a unique string for deduplication
      const rangeString = `${machemetryId}_${finalRange.ge}_${finalRange.lt}`;

      // Skip if this is the same range we just processed
      if (rangeString === lastRangeStringRef.current) {
        console.log(
          `[DataDumpRows] Skipping duplicate range request for ${machemetryId}`
        );
        throttleTimerRef.current = null;
        return;
      }

      console.log(
        `[DataDumpRows] Throttle complete, executing fetch for ${machemetryId} with range:`,
        finalRange
      );
      lastRangeStringRef.current = rangeString;

      // If the process ID changed, we need to reset our tracking
      if (machemetryId !== ticksRanges.processId) {
        setTicksRanges({ processId: machemetryId, ranges: [] });
        setMachemetryTicks({
          grossCount_0: [],
          rejectCount_0: [],
          netCount_0: [],
        });
      }

      // Calculate the missing ranges we need to fetch
      const existingRanges = ticksRanges.ranges;
      const missingRanges = getMissingRanges(existingRanges, finalRange);

      // If we have all the data we need, don't make new requests
      if (missingRanges.length === 0) {
        console.log(
          `[DataDumpRows] No missing ranges to fetch for ${machemetryId}`
        );
        throttleTimerRef.current = null;
        return;
      }

      // Create a map to track which ranges have been successfully fetched
      // We'll only update ticksRanges after successful fetches
      const rangeFetchStatus = new Map();
      missingRanges.forEach(range => {
        const rangeKey = `${range.ge}-${range.lt}`;
        rangeFetchStatus.set(rangeKey, false);
      });

      // Store the current lastFetchedTime
      lastFetchedTime.current = Date.now() / 1000;

      // Log for debugging
      console.log(
        `[DataDumpRows] Fetching ${missingRanges.length} missing tick ranges:`,
        missingRanges
      );

      // Fetch each missing range
      missingRanges.forEach(range => {
        const debugUrls = Object.keys(SIGNALS_DEBUG_TIMELINES).map(signal => ({
          name: signal,
          url: `${MACHEMETRY_URL}v2/measurements?s=${machemetryId}_${signal}&gte=${range.ge}&lt=${range.lt}`,
        }));

        debugUrls.forEach(({ url, name }) => {
          fetch(url, {
            method: "GET",
            headers: {
              authorization: idToken ? `Bearer ${idToken}` : "no-token",
            },
          })
            .then(response => {
              if (!response.ok) {
                throw new Error(
                  `HTTP error ${response.status}: ${response.statusText}`
                );
              }
              return response.json();
            })
            .then(d => {
              // Mark this range as successfully fetched
              const rangeKey = `${range.ge}-${range.lt}`;
              rangeFetchStatus.set(rangeKey, true);

              // Only update ticksRanges for this specific range if all data was fetched successfully
              startTransition(() => {
                // Add this successfully fetched range to our tracking
                setTicksRanges(prev => ({
                  processId: machemetryId,
                  ranges: mergeOverlappingRanges([...prev.ranges, range]),
                }));
              });

              if (d[`${machemetryId}_${name}`]?.length) {
                // Store new data along with existing data, preserving any existing ticks
                startTransition(() => {
                  setMachemetryTicks(prev => {
                    const combinedData = [
                      ...(prev[name] || []),
                      ...d[`${machemetryId}_${name}`],
                    ];
                    const uniqueData = removeDuplicateTicks(combinedData);

                    console.log(
                      `[DataDumpRows] Received ${d[`${machemetryId}_${name}`].length} ticks for ${name}, total now: ${uniqueData.length}`
                    );

                    return {
                      ...prev,
                      [name]: uniqueData,
                    };
                  });
                });
              }
            })
            .catch(error => {
              console.error(
                `[DataDumpRows] Error fetching signal data for range ${range.ge}-${range.lt}:`,
                error
              );
              // Critical: Don't update ticksRanges for failed fetches
              // This ensures we'll try again for this range in the future
            });
        });
      });

      // Clear timer reference
      throttleTimerRef.current = null;
    }, THROTTLE_WAIT);

    return () => {
      // Do not clear the timer on unmount - let it complete
      // This ensures we always process the most recent range request
    };
  }, [
    visibleRange,
    machemetryId,
    ticksRanges,
    setTicksRanges,
    setMachemetryTicks,
    idToken,
  ]);

  // This useEffect is no longer needed as we're handling the refresh in the isCurrentTimeInVisibleRange effect

  // DO NOT clear ticks when component unmounts
  // Only clear when process ID changes or explicitly requested
  // This prevents ticks from disappearing after initial load

  return null;
}

// Helper function to remove duplicate ticks based on timestamp
function removeDuplicateTicks(ticks) {
  const uniqueMap = new Map();
  ticks.forEach(tick => {
    if (!uniqueMap.has(tick.ts) || tick.d > (uniqueMap.get(tick.ts)?.d || 0)) {
      uniqueMap.set(tick.ts, tick);
    }
  });
  return Array.from(uniqueMap.values()).sort((a, b) => a.ts - b.ts);
}

// Helper function to identify what time ranges we haven't fetched yet
function getMissingRanges(
  existingRanges: { ge: number; lt: number }[],
  targetRange: { ge: number; lt: number }
): { ge: number; lt: number }[] {
  if (existingRanges.length === 0) {
    return [targetRange];
  }

  // Check if the target range is completely contained in any existing range
  const isContained = existingRanges.some(
    range => range.ge <= targetRange.ge && range.lt >= targetRange.lt
  );

  if (isContained) {
    return [];
  }

  // Find all ranges that overlap with the target range
  const overlappingRanges = existingRanges.filter(
    range => !(range.lt <= targetRange.ge || range.ge >= targetRange.lt)
  );

  if (overlappingRanges.length === 0) {
    // No overlap, need to fetch the entire target range
    return [targetRange];
  }

  // Create a sorted list of all boundaries
  const boundaries = [targetRange.ge, targetRange.lt];
  overlappingRanges.forEach(range => {
    if (range.ge > targetRange.ge && range.ge < targetRange.lt) {
      boundaries.push(range.ge);
    }
    if (range.lt > targetRange.ge && range.lt < targetRange.lt) {
      boundaries.push(range.lt);
    }
  });

  // Sort boundaries
  boundaries.sort((a, b) => a - b);

  // Create missing ranges
  const missing: { ge: number; lt: number }[] = [];
  for (let i = 0; i < boundaries.length - 1; i++) {
    const ge = boundaries[i];
    const lt = boundaries[i + 1];

    // Check if this segment is already covered
    const isSegmentCovered = overlappingRanges.some(
      range => range.ge <= ge && range.lt >= lt
    );

    if (!isSegmentCovered) {
      missing.push({ ge, lt });
    }
  }

  return missing;
}

// Helper function to merge overlapping time ranges
function mergeOverlappingRanges(
  ranges: { ge: number; lt: number }[]
): { ge: number; lt: number }[] {
  if (ranges.length <= 1) {
    return ranges;
  }

  // Sort ranges by start time
  const sortedRanges = [...ranges].sort((a, b) => a.ge - b.ge);

  const merged: { ge: number; lt: number }[] = [];
  let current = sortedRanges[0];

  for (let i = 1; i < sortedRanges.length; i++) {
    const next = sortedRanges[i];

    // If current range overlaps with the next one, extend current
    if (current.lt >= next.ge) {
      current = { ge: current.ge, lt: Math.max(current.lt, next.lt) };
    } else {
      // No overlap, add current to result and move to next
      merged.push(current);
      current = next;
    }
  }

  // Add the last range
  merged.push(current);

  return merged;
}
